import { useCreateEmployees } from "@/hooks/use-create-employees";
import { useMigrateEmployees } from "@/hooks/use-migrate-employees";
import { useCallback } from "react";
import { Member } from "../types";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import { useSetupSummary } from "../context/setup-summary-context";
import { RouterOutput } from "@/api/client";

export type CreateEmployeeError =
  RouterOutput["createEmployees"]["errors"][number];

interface UseCreateMembersProps {
  onCreateError?: (errors: CreateEmployeeError[]) => void;
}

export const useCreateMembers = ({
  onCreateError,
}: UseCreateMembersProps = {}) => {
  const { selectedCompany } = useSelectedCompany();
  const { incrementMembersCreated } = useSetupSummary();
  const { createEmployees, isLoading: createEmployeeLoading } =
    useCreateEmployees({});
  const { migrateEmployeesCompany, isLoading: migrateEmployeeLoading } =
    useMigrateEmployees({});

  const createMembers = useCallback(
    async (members: Member[]) => {
      const employeesToMigrate = members
        .filter((member) => member.type === "MIGRATE")
        .map((member) => ({
          employeeId: member.id,
          companyId: selectedCompany.id,
        }));
      const employeesToCreate = members.filter(
        (member) => member.type === "CREATE"
      );

      try {
        const employeesPromises = [];
        if (employeesToCreate.length) {
          employeesPromises.push(
            createEmployees(employeesToCreate).then((response) => {
              if (response.errors.length) {
                onCreateError?.(response.errors);
              }
            })
          );
        }

        if (employeesToMigrate.length) {
          employeesPromises.push(migrateEmployeesCompany(employeesToMigrate));
        }

        await Promise.allSettled(employeesPromises);

        incrementMembersCreated(members.length);
      } catch (error) {
        console.error(error);
      }
    },
    [
      onCreateError,
      createEmployees,
      incrementMembersCreated,
      migrateEmployeesCompany,
      selectedCompany.id,
    ]
  );

  return {
    createMembers,
    isLoading: createEmployeeLoading || migrateEmployeeLoading,
  };
};
